import React from "react";
import {
  getPassiveLevel,
  safeNum,
  formatNumber,
  formatCurrency,
} from "./passiveLoadHelpers";

const InfoIcon = ({ text }) => (
  <span className="metric-info-icon" title={text} aria-label={text}>
    <i className="bi bi-info-circle" />
  </span>
);

const MetricLabel = ({ children, info }) => (
  <span className="metric-label">
    <span>{children}</span>
    <InfoIcon text={info} />
  </span>
);

const HeaderWithInfo = ({ children, info, className = "" }) => (
  <th className={className}>
    <span className="header-with-info">
      <span>{children}</span>
      <InfoIcon text={info} />
    </span>
  </th>
);

const TitleWithInfo = ({ children, info }) => (
  <span className="title-with-info">
    <span>{children}</span>
    <InfoIcon text={info} />
  </span>
);

const PassiveLoadTable = ({ data }) => {
  if (!data || !data.totals) return null;

  const { totals } = data;
  const totalEnergy = safeNum(totals.totalEnergy, 0);

  const nonProductiveEnergy =
    safeNum(totals.nonProductiveEnergy, 0) ||
    safeNum(totals.passiveNightEnergy, 0);

  const passiveBaseEnergy =
    safeNum(totals.passiveBaseEnergy, 0) ||
    safeNum(totals.passiveNightEnergy, 0);

  const elevatedNonProductiveEnergy = safeNum(
    totals.elevatedNonProductiveEnergy ??
      totals.nonProductiveAnomalyEnergy ??
      0,
    0
  );

  const baseLoadEnergy = safeNum(totals.baseLoadEnergy ?? totals.baseLoad, 0);

  const activeEnergy = totalEnergy - nonProductiveEnergy;
  const activePercentage =
    totalEnergy > 0 ? (activeEnergy / totalEnergy) * 100 : 0;

  const costTotal = totals.costTotal ?? 0;
  const costPassive = totals.cost ?? totals.costPassive ?? 0;
  const costActive = totals.costActive ?? Math.max(costTotal - costPassive, 0);

  const baseLoadCost =
    totals.baseLoadCost ??
    (totals.tariff != null
      ? baseLoadEnergy * (totals.tariff ?? 0)
      : totals.baseLoadCost ?? 0);

  const costPassivePercentage =
    costTotal > 0 ? (costPassive / costTotal) * 100 : 0;

  const costActivePercentage =
    costTotal > 0 ? (costActive / costTotal) * 100 : 0;

  const passivePct =
    totals.passivePercentage ??
    (totalEnergy > 0 ? (nonProductiveEnergy / totalEnergy) * 100 : null);

  const passiveLevel = getPassiveLevel(passivePct);

  return (
    <div className="passive-load-summary">
      <h3>
        <TitleWithInfo info="Resumen de energía y costos asociados al consumo fuera de horario, consumo productivo y línea base del período analizado.">
          Resumen energético y económico
        </TitleWithInfo>
      </h3>

      <div className="passive-summary-table">
        <table>
          <thead>
            <tr>
              <HeaderWithInfo
                className="col-metric"
                info="Nombre del indicador calculado para el período analizado."
              >
                Métrica
              </HeaderWithInfo>

              <HeaderWithInfo
                className="col-energy"
                info="Energía acumulada del indicador, expresada en kWh."
              >
                Energía (kWh)
              </HeaderWithInfo>

              <HeaderWithInfo
                className="col-energy-pct"
                info="Participación de esa energía sobre el total analizado."
              >
                % Energía
              </HeaderWithInfo>

              <HeaderWithInfo
                className="col-cost"
                info="Costo estimado asociado al consumo de energía del indicador."
              >
                Costo [$]
              </HeaderWithInfo>

              <HeaderWithInfo
                className="col-cost-pct"
                info="Participación de ese costo sobre el costo total estimado."
              >
                % Costo
              </HeaderWithInfo>
            </tr>
          </thead>

          <tbody>
            <tr className="row-total">
              <td className="metric-name">
                <MetricLabel info="Energía total del período seleccionado usada como base del análisis.">
                  Consumo total
                </MetricLabel>
              </td>

              <td className="value-energy">
                <strong>{formatNumber(totalEnergy)}</strong>
              </td>

              <td className="value-pct">
                <strong>100</strong>
              </td>

              <td className="value-cost">
                <strong>{formatCurrency(costTotal)}</strong>
              </td>

              <td className="value-pct">
                <strong>100</strong>
              </td>
            </tr>

            <tr className="row-passive">
              <td className="metric-name">
                <MetricLabel info="Toda la energía consumida fuera del horario productivo configurado.">
                  Consumo fuera de horario
                </MetricLabel>
                <div className="metric-sub">{passiveLevel}</div>
              </td>

              <td className="value-energy">
                {formatNumber(nonProductiveEnergy)}
              </td>

              <td className="value-pct">
                {passivePct != null ? formatNumber(passivePct, 2) : "-"}
              </td>

              <td className="value-cost">{formatCurrency(costPassive)}</td>

              <td className="value-pct">
                {formatNumber(costPassivePercentage, 2)}
              </td>
            </tr>

            <tr className="row-passive-base">
              <td className="metric-name">
                <MetricLabel info="Parte del consumo fuera de horario que coincide con la línea base esperada del dispositivo. Representa el consumo mínimo normal.">
                  Consumo base fuera de horario
                </MetricLabel>
              </td>

              <td className="value-energy">
                {formatNumber(passiveBaseEnergy)}
              </td>

              <td className="value-pct">
                {totalEnergy > 0
                  ? formatNumber((passiveBaseEnergy / totalEnergy) * 100, 2)
                  : "-"}
              </td>

              <td className="value-cost">-</td>

              <td className="value-pct">-</td>
            </tr>

            <tr className="row-elevated">
              <td className="metric-name">
                <MetricLabel info="Parte del consumo fuera de horario que supera la línea base y el margen permitido. Es el exceso que conviene revisar.">
                  Consumo elevado fuera de horario
                </MetricLabel>
              </td>

              <td className="value-energy">
                {formatNumber(elevatedNonProductiveEnergy)}
              </td>

              <td className="value-pct">
                {totalEnergy > 0
                  ? formatNumber(
                      (elevatedNonProductiveEnergy / totalEnergy) * 100,
                      2
                    )
                  : "-"}
              </td>

              <td className="value-cost">-</td>

              <td className="value-pct">-</td>
            </tr>

            <tr className="row-active">
              <td className="metric-name">
                <MetricLabel info="Energía consumida durante el horario productivo configurado. Representa el consumo asociado a la operación normal.">
                  Consumo productivo
                </MetricLabel>
              </td>

              <td className="value-energy">{formatNumber(activeEnergy)}</td>

              <td className="value-pct">
                {formatNumber(activePercentage, 2)}
              </td>

              <td className="value-cost">{formatCurrency(costActive)}</td>

              <td className="value-pct">
                {formatNumber(costActivePercentage, 2)}
              </td>
            </tr>

            <tr className="row-base-load">
              <td className="metric-name">
                <MetricLabel info="Energía estimada asociada a la línea base durante todo el período. Ayuda a dimensionar el consumo constante del dispositivo.">
                  Línea base acumulada
                </MetricLabel>
              </td>

              <td className="value-energy">
                {formatNumber(baseLoadEnergy)}
              </td>

              <td className="value-pct">
                {totalEnergy > 0
                  ? formatNumber((baseLoadEnergy / totalEnergy) * 100, 2)
                  : "-"}
              </td>

              <td className="value-cost">{formatCurrency(baseLoadCost)}</td>

              <td className="value-pct">
                {costTotal > 0
                  ? formatNumber((baseLoadCost / costTotal) * 100, 2)
                  : "-"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PassiveLoadTable;